import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListPlus } from "lucide-react";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STATUS = {
  resolved: ["TROUVÉE", "text-emerald-700 dark:text-emerald-400"],
  code_postal: ["CODE POSTAL", "text-sky-700 dark:text-sky-400"],
  ambiguous: ["À PRÉCISER", "text-amber-700 dark:text-amber-400"],
  not_found: ["INTROUVABLE", "text-red-700 dark:text-red-400"],
  error: ["ERREUR", "text-red-700 dark:text-red-400"],
};

const Candidate = ({ c, checked, onToggle }) => (
  <label className="flex items-center gap-2 text-xs py-0.5 pl-3">
    <input type="checkbox" data-testid={`bulk-select-${c.insee}`} disabled={c.monitored} checked={c.monitored || checked} onChange={onToggle} />
    <span className="font-medium">{c.name}</span>
    <span className="font-mono text-[10px] text-slate-500">{c.insee} · {c.postal_codes.join(", ")} · dép. {c.department_code}{c.match ? ` · via ${c.match}` : ""}</span>
    {c.monitored && <span className="ml-auto text-[10px] text-emerald-700 font-mono">DÉJÀ SURVEILLÉE</span>}
  </label>
);

export const BulkAddCommunes = ({ onAdded }) => {
  const [text, setText] = useState("");
  const [dep, setDep] = useState("");
  const [terr, setTerr] = useState("");
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const { data: territories } = useQuery({ queryKey: ["territories"], queryFn: () => get("/territories") });

  const resolve = async () => {
    setBusy(true);
    try {
      const r = await post("/communes/bulk-resolve", { text, department: dep || null });
      setRes(r.results);
      setSel(r.results.flatMap((x) => x.candidates.filter((c) => c.preselect && !c.monitored).map((c) => c.insee)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de recherche"); }
    setBusy(false);
  };
  const toggle = (code) => setSel(sel.includes(code) ? sel.filter((x) => x !== code) : [...sel, code]);
  const add = async () => {
    setBusy(true);
    const r = await post("/communes", { insee_codes: sel, territory_id: terr || null });
    setBusy(false);
    toast.success(`${r.added.length} commune(s) ajoutée(s) à la surveillance${terr ? " et au territoire" : ""}`);
    setRes(null); setSel([]); setText("");
    onAdded();
  };

  return (
    <div className="panel p-3" data-testid="bulk-add-panel">
      <div className="overline mb-2 flex items-center gap-1.5"><ListPlus className="h-3.5 w-3.5" />Ajout groupé — plusieurs communes à la fois</div>
      <Textarea data-testid="bulk-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} className="text-xs"
        placeholder="Noms, codes postaux ou codes INSEE séparés par des virgules, points-virgules ou retours à la ligne — ex. Genlis, Auxonne, Brazey-en-Plaine, 21110" />
      <div className="flex gap-2 mt-2">
        <Input data-testid="bulk-department" value={dep} onChange={(e) => setDep(e.target.value)} placeholder="Département (optionnel, ex. 21)" className="h-8 text-xs w-48" />
        <Button data-testid="bulk-resolve-btn" size="sm" className="h-8" disabled={!text.trim() || busy} onClick={resolve}>Vérifier les communes</Button>
      </div>
      {res && (
        <div className="mt-3 space-y-1.5" data-testid="bulk-results">
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {res.map((r) => (
              <div key={r.query} data-testid={`bulk-result-${r.query}`} className="border-b border-slate-100 dark:border-slate-800 pb-1">
                <div className="text-xs flex gap-2"><span className="font-semibold">« {r.query} »</span><span className={`font-mono text-[10px] ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>
                  {r.status === "ambiguous" && <span className="text-[10px] text-slate-500">plusieurs communes possibles — cochez la bonne</span>}</div>
                {r.candidates.map((c) => <Candidate key={c.insee} c={c} checked={sel.includes(c.insee)} onToggle={() => toggle(c.insee)} />)}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <select data-testid="bulk-territory" value={terr} onChange={(e) => setTerr(e.target.value)} className="h-7 border border-slate-200 dark:border-slate-700 rounded-[3px] bg-transparent text-xs">
              <option value="">Sans territoire</option>
              {(territories || []).map((t) => <option key={t.id} value={t.id}>Ajouter aussi au territoire : {t.name}</option>)}
            </select>
            <Button data-testid="bulk-add-btn" size="sm" className="h-7 text-xs" disabled={!sel.length || busy} onClick={add}>Ajouter {sel.length} commune(s) à la surveillance</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setRes(null); setSel([]); }}>Annuler</Button>
          </div>
        </div>
      )}
    </div>
  );
};
